import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Parameter definitions
const parameterDefinitions = [
  { key: "line", label: "Line" },
  { key: "machine", label: "Machine" },
  { key: "from", label: "From" },
  { key: "to", label: "To" },
  { key: "issue", label: "Issue" },
];

const defaultSection = {
  line: "N/A",
  machine: "N/A",
  from: "N/A",
  to: "N/A",
  issue: "N/A",
};

const sectionCount = 5;

const MachineBreakdownHistoryTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Build lookup map for sections: `${date}_${shift}`
  const dataMap = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;

      const key = `${date}_${shift}`;
      const sections = item.form_data?.sections || [];
      map[key] = Array(sectionCount)
        .fill(null)
        .map((_, i) => sections[i] || defaultSection);
    });
    return map;
  }, [data]);

  // Hierarchical header structure: Date -> Shift -> Section 1 to 5
  const headerStructure = useMemo(() => {
    return dates.map((date) => {
      const shiftNodes = effectiveShifts.map((shift) => {
        const key = `${date}_${shift}`;
        const sections = Array.from({ length: sectionCount }, (_, i) => ({
          id: `${key}_section_${i + 1}`,
          label: `Section ${i + 1}`,
          date,
          shift,
          sectionIndex: i,
        }));

        return {
          shift,
          key,
          colSpan: sectionCount,
          sections,
        };
      });

      return {
        date,
        colSpan: effectiveShifts.length * sectionCount,
        shifts: shiftNodes,
      };
    });
  }, [dates, effectiveShifts]);

  // Flattened leaf columns for the table body
  const flatColumns = useMemo(() => {
    return headerStructure.flatMap((d) => d.shifts.flatMap((s) => s.sections));
  }, [headerStructure]);

  // Column width calculations
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: 180,
      value: 120,
    };

    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.max(widths.parameter, estimateTextWidth(param.label));
    });

    return widths;
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Static parameter rows
  const tableRows = useMemo(() => {
    return parameterDefinitions.map((param) => ({
      key: param.key,
      parameter: param.label,
    }));
  }, []);

  // Lookup map: `${date}_${shift}_${sectionIndex}_${paramKey}`
  const tableDataMap = useMemo(() => {
    const map = {};
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const sections = dataMap[key] || Array(sectionCount).fill(defaultSection);

        sections.forEach((sec, i) => {
          parameterDefinitions.forEach((param) => {
            const val = sec ? sec[param.key] : "N/A";
            map[`${key}_${i}_${param.key}`] = val && val !== "" ? String(val) : "N/A";
          });
        });
      });
    });
    return map;
  }, [dates, effectiveShifts, dataMap]);

  const colsPerShift = sectionCount;
  const colsPerDate = effectiveShifts.length * sectionCount;

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Parameter"];
    const row2 = [""];
    const row3 = [""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 2, c: 0 } });

    let colIdx = 1;

    headerStructure.forEach((dateNode) => {
      row1.push(dateNode.date);
      for (let i = 1; i < colsPerDate; i++) row1.push("");
      if (colsPerDate > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + colsPerDate - 1 } });
      }

      dateNode.shifts.forEach((shiftNode) => {
        row2.push(`Shift ${shiftNode.shift}`);
        for (let i = 1; i < colsPerShift; i++) row2.push("");
        if (colsPerShift > 1) {
          merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + colsPerShift - 1 } });
        }

        shiftNode.sections.forEach((sec) => {
          row3.push(sec.label);
          colIdx += 1;
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      flatColumns.forEach((col) => {
        const val = tableDataMap[`${col.date}_${col.shift}_${col.sectionIndex}_${row.key}`];
        r.push(val !== undefined ? val : "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, row3, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 22 },
      ...Array(flatColumns.length).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Machine Breakdown");

    const fileName = generateExcelFileName("Machine_Breakdown_History", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", bookSST: false });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Machine Breakdown History
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Parameter + Dates (merged across shifts and their sections) */}
            <TableRow>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {headerStructure.map((dateNode) => (
                <CustomTableCell
                  key={dateNode.date}
                  colSpan={dateNode.colSpan}
                  columnWidth={columnWidths.value * dateNode.colSpan}
                >
                  {dateNode.date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shifts (sub-columns under Date) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.map((shiftNode) => (
                  <CustomTableCell
                    key={`${dateNode.date}_${shiftNode.shift}`}
                    colSpan={shiftNode.colSpan}
                    columnWidth={columnWidths.value * shiftNode.colSpan}
                  >
                    Shift {shiftNode.shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Sections (sub-columns under Shift) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.flatMap((shiftNode) =>
                  shiftNode.sections.map((sec) => (
                    <CustomTableCell
                      key={sec.id}
                      columnWidth={columnWidths.value}
                    >
                      {sec.label}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={1}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>

                  {flatColumns.map((col) => {
                    const val = tableDataMap[`${col.date}_${col.shift}_${col.sectionIndex}_${row.key}`];
                    return (
                      <BodyTableCell key={`${col.id}_${row.key}`} columnWidth={columnWidths.value}>
                        {val !== undefined ? val : "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default MachineBreakdownHistoryTable;